import { useCallback, useEffect, useRef, useState } from 'react';
import { differenceInHours } from 'date-fns';
import type { ScheduleEvent, DateRange, EmbeddedToHostMessage } from '../types';
import type { ProviderMatchingHandle } from '../matching/matchingTypes';
import type { SlotOpenedPayload } from '../waitlist/waitlistTypes';
import type {
  NoShowRiskConfig,
  NoShowRiskHandle,
  NoShowRiskMachineState,
  AppointmentReminderState,
  AppointmentRiskScore,
  RiskScoresPayload,
  ReminderOutcomePayload,
  AppointmentStatusUpdatePayload,
  RiskCommandPayload,
  ReminderScheduleConfig,
  ReminderTiming,
} from './riskTypes';
import { DEFAULT_REMINDER_SCHEDULE } from './riskTypes';

const SCORE_TIMEOUT_MS = 5_000;
const SCHEDULER_TICK_MS = 300_000; // 5 minutes

function resolveSchedule(
  riskLevel: 'low' | 'moderate' | 'high' | 'critical',
  globalSchedule: ReminderScheduleConfig,
  override?: ReminderScheduleConfig,
): { reminders: ReminderTiming[]; requireConfirmation: boolean; flagForOutreach: boolean } {
  const src = override ?? globalSchedule;
  return src[riskLevel];
}

export function useNoShowRisk(
  config: NoShowRiskConfig | undefined,
  emit: (msg: EmbeddedToHostMessage) => void,
  viewRange: DateRange,
  events: ScheduleEvent[],
  matchingHandle: ProviderMatchingHandle,
  onSlotOpened: (payload: SlotOpenedPayload) => void,
): NoShowRiskHandle {
  const enabled = config?.enabled ?? false;

  const [machineState, setMachineState] = useState<NoShowRiskMachineState>('IDLE');
  const [riskScoresMap, setRiskScoresMap] = useState<Map<string, AppointmentRiskScore>>(new Map());

  // Reminder state survives date-range navigation — scores re-fetch but fired history persists.
  const reminderStateRef = useRef<Map<string, AppointmentReminderState>>(new Map());

  const machineStateRef = useRef<NoShowRiskMachineState>('IDLE');
  const configRef = useRef(config);
  const eventsRef = useRef(events);
  const viewRangeRef = useRef(viewRange);
  const matchingHandleRef = useRef(matchingHandle);
  const onSlotOpenedRef = useRef(onSlotOpened);
  // Forward ref so requestRiskScores can call onRiskScoresReceived without circular dependency.
  const onRiskScoresReceivedRef = useRef<(p: RiskScoresPayload) => void>(() => undefined);

  useEffect(() => { configRef.current = config; }, [config]);
  useEffect(() => { eventsRef.current = events; }, [events]);
  useEffect(() => { viewRangeRef.current = viewRange; }, [viewRange]);
  useEffect(() => { matchingHandleRef.current = matchingHandle; }, [matchingHandle]);
  useEffect(() => { onSlotOpenedRef.current = onSlotOpened; }, [onSlotOpened]);

  const transition = useCallback((next: NoShowRiskMachineState) => {
    machineStateRef.current = next;
    setMachineState(next);
  }, []);

  const requestRiskScores = useCallback(() => {
    if (!configRef.current?.enabled) return;
    const currentEvents = eventsRef.current;
    const vr = viewRangeRef.current;

    // Score all non-queued events in the visible range (past and future).
    // The reminder tick skips past appointments; badges show for all.
    const upcoming = currentEvents.filter((e) => {
      if (!e.startUtc || e.queued) return false;
      const start = new Date(e.startUtc);
      return start >= new Date(vr.startUtc) && start <= new Date(vr.endUtc);
    });

    const appointments = upcoming.map((e) => ({
      appointmentId: e.id,
      patientId: (e as unknown as Record<string, unknown>)['patientId'] as string ?? e.id,
      resourceId: e.resourceId ?? '',
      start: e.startUtc,
      end: e.endUtc,
      appointmentType: (e as unknown as Record<string, unknown>)['appointmentType'] as string ?? 'appointment',
      screenerSeverity: e.screenerSeverity as ('low' | 'moderate' | 'high' | 'crisis') | undefined,
    }));

    const payload = {
      visibleRangeStart: vr.startUtc,
      visibleRangeEnd: vr.endUtc,
      appointments,
    };

    const result = configRef.current.onRequestRiskScores(payload);
    emit({ type: 'RISK_SCORES_REQUEST', payload });
    transition('AWAITING_SCORES');

    // Direct-React mode: if the host returns a Promise, apply scores immediately.
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      (result as Promise<RiskScoresPayload>).then((scores) => {
        if (machineStateRef.current === 'AWAITING_SCORES') {
          onRiskScoresReceivedRef.current(scores);
        }
      }).catch(() => {
        if (machineStateRef.current === 'AWAITING_SCORES') transition('IDLE');
      });
    }
  }, [emit, transition]);

  // Fire RISK_SCORES_REQUEST on mount, view range changes, and when events load.
  const prevRangeKey = useRef('');
  const prevEventCount = useRef(-1);
  useEffect(() => {
    if (!enabled) return;
    const rangeKey = `${viewRange.startUtc}|${viewRange.endUtc}`;
    const rangeChanged = prevRangeKey.current !== rangeKey;
    const eventsLoaded = prevEventCount.current === 0 && events.length > 0;
    if (!rangeChanged && !eventsLoaded) return;
    prevRangeKey.current = rangeKey;
    prevEventCount.current = events.length;
    requestRiskScores();
  }, [enabled, viewRange, events, requestRiskScores]);

  // Timeout: fall back to IDLE silently if no RISK_SCORES arrives within 5s.
  useEffect(() => {
    if (machineState !== 'AWAITING_SCORES') return;
    const id = setTimeout(() => {
      if (machineStateRef.current === 'AWAITING_SCORES') transition('IDLE');
    }, SCORE_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [machineState, transition]);

  // 5-minute scheduler tick.
  useEffect(() => {
    if (!enabled) return;
    const tick = () => {
      if (machineStateRef.current !== 'RISK_ACTIVE') return;
      const cfg = configRef.current;
      if (!cfg) return;
      const globalSchedule = cfg.reminderSchedule ?? DEFAULT_REMINDER_SCHEDULE;
      const now = new Date();

      for (const [, state] of reminderStateRef.current) {
        if (state.suppressReminders) continue;
        if (state.status === 'cancelled' || state.status === 'rescheduled') continue;

        const appointmentStart = new Date(state.appointmentStart);
        if (appointmentStart <= now) continue;

        const hoursUntil = differenceInHours(appointmentStart, now);

        state.schedule.forEach((timing, idx) => {
          if (state.firedReminders.includes(timing.hoursBeforeAppointment)) return;
          if (hoursUntil > timing.hoursBeforeAppointment) return;

          // Fire this reminder.
          const reminderNumber = idx + 1;
          const totalReminders = state.schedule.length;
          const payload = {
            appointmentId: state.appointmentId,
            patientId: state.patientId,
            resourceId: state.resourceId,
            appointmentStart: state.appointmentStart,
            riskLevel: state.riskLevel,
            riskScore: state.riskScore,
            reminderNumber,
            totalReminders,
            channel: timing.channel,
            requireConfirmation: state.requireConfirmation,
            flagForOutreach: state.flagForOutreach,
            hoursUntilAppointment: hoursUntil,
          };

          cfg.onReminderDue(payload);
          emit({ type: 'REMINDER_DUE', payload });
          state.firedReminders.push(timing.hoursBeforeAppointment);
          state.lastReminderOutcome = 'due';
        });

        // Emit OUTREACH_FLAGGED once when flagForOutreach threshold is reached.
        if (state.flagForOutreach && !state.outreachFlagged) {
          const tier = globalSchedule[state.riskLevel];
          const outreachThreshold = tier.reminders[0]?.hoursBeforeAppointment ?? Infinity;
          if (hoursUntil <= outreachThreshold) {
            const matchLevel = matchingHandleRef.current?.matches
              ?.find((m) => m.resourceId === state.resourceId)?.matchLevel;

            const outreachPayload = {
              appointmentId: state.appointmentId,
              patientId: state.patientId,
              resourceId: state.resourceId,
              appointmentStart: state.appointmentStart,
              riskLevel: state.riskLevel,
              riskScore: state.riskScore,
              riskFactors: state.riskFactors,
              remindersSentCount: state.firedReminders.length,
              confirmed: state.status === 'confirmed',
              matchLevel,
            };
            emit({ type: 'OUTREACH_FLAGGED', payload: outreachPayload });
            state.outreachFlagged = true;
          }
        }
      }
    };

    tick();
    const id = setInterval(tick, SCHEDULER_TICK_MS);
    return () => clearInterval(id);
  }, [enabled, emit]);

  const onRiskScoresReceived = useCallback((payload: RiskScoresPayload) => {
    const cfg = configRef.current;
    if (!cfg) return;
    const globalSchedule = cfg.reminderSchedule ?? DEFAULT_REMINDER_SCHEDULE;
    const newMap = new Map<string, AppointmentRiskScore>();

    for (const score of payload.scores) {
      newMap.set(score.appointmentId, score);

      // Update or create reminder state — preserve fired history if it already exists.
      const existing = reminderStateRef.current.get(score.appointmentId);
      const tier = resolveSchedule(score.riskLevel, globalSchedule, score.reminderScheduleOverride);
      const event = eventsRef.current.find((e) => e.id === score.appointmentId);

      reminderStateRef.current.set(score.appointmentId, {
        appointmentId: score.appointmentId,
        patientId: (event as unknown as Record<string, unknown> | undefined)?.['patientId'] as string ?? score.appointmentId,
        resourceId: event?.resourceId ?? '',
        appointmentStart: event?.startUtc ?? '',
        riskLevel: score.riskLevel,
        riskScore: score.riskScore,
        riskFactors: score.riskFactors,
        suppressReminders: score.suppressReminders ?? false,
        schedule: tier.reminders,
        requireConfirmation: tier.requireConfirmation,
        flagForOutreach: tier.flagForOutreach,
        firedReminders: existing?.firedReminders ?? [],
        outreachFlagged: existing?.outreachFlagged ?? false,
        status: existing?.status ?? 'pending',
        lastReminderOutcome: existing?.lastReminderOutcome,
        lastDeliveredAt: existing?.lastDeliveredAt,
      });
    }

    setRiskScoresMap(newMap);
    transition('RISK_ACTIVE');
  }, [transition]);

  useEffect(() => { onRiskScoresReceivedRef.current = onRiskScoresReceived; }, [onRiskScoresReceived]);

  const onReminderOutcomeReceived = useCallback((payload: ReminderOutcomePayload) => {
    const state = reminderStateRef.current.get(payload.appointmentId);
    if (!state) return;
    state.lastReminderOutcome = payload.delivered ? 'delivered' : 'failed';
    if (payload.delivered) state.lastDeliveredAt = payload.deliveredAt;
    // Force a re-render to update delivery icons.
    setRiskScoresMap((prev) => new Map(prev));
  }, []);

  const onAppointmentStatusUpdateReceived = useCallback((payload: AppointmentStatusUpdatePayload) => {
    const cfg = configRef.current;
    const state = reminderStateRef.current.get(payload.appointmentId);

    cfg?.onReminderResponse?.(payload);

    if (payload.status === 'cancelled') {
      // Fire SLOT_OPENED for Phase 3 backfill before clearing local state.
      const event = eventsRef.current.find((e) => e.id === payload.appointmentId);
      if (event) {
        const vr = viewRangeRef.current;
        const durationMs = new Date(event.endUtc).getTime() - new Date(event.startUtc).getTime();
        const matchLevel = matchingHandleRef.current?.matches
          ?.find((m) => m.resourceId === event.resourceId)?.matchLevel;

        const slotPayload: SlotOpenedPayload = {
          slotId: crypto.randomUUID(),
          resourceId: event.resourceId ?? '',
          start: event.startUtc,
          end: event.endUtc,
          duration: Math.round(durationMs / 60000),
          openedBy: 'cancellation',
          previousAppointmentId: event.id,
          screenerSeverity: event.screenerSeverity as SlotOpenedPayload['screenerSeverity'],
          visibleRangeStart: vr.startUtc,
          visibleRangeEnd: vr.endUtc,
          riskLevel: state?.riskLevel,
          matchLevel,
        };
        onSlotOpenedRef.current(slotPayload);
      }
    }

    if (state) {
      state.status = payload.status === 'confirmed'
        ? 'confirmed'
        : payload.status === 'rescheduled'
          ? 'rescheduled'
          : payload.status === 'cancelled'
            ? 'cancelled'
            : state.status;
      // Cancel remaining scheduled reminders.
      if (payload.status !== 'no_response') {
        state.firedReminders = state.schedule.map((t) => t.hoursBeforeAppointment);
      }
    }

    setRiskScoresMap((prev) => new Map(prev));
  }, []);

  const handleRiskCommand = useCallback((payload: RiskCommandPayload) => {
    configRef.current?.onRiskCommand?.(payload);
    emit({ type: 'RISK_COMMAND', payload });
    // Re-request scores after a command so the UI refreshes.
    requestRiskScores();
  }, [emit, requestRiskScores]);

  const getReminderState = useCallback(
    (appointmentId: string) => reminderStateRef.current.get(appointmentId),
    [],
  );

  if (!enabled) {
    return {
      enabled: false,
      machineState: 'IDLE',
      riskScoresMap: new Map(),
      getReminderState: () => undefined,
      requestRiskScores: () => undefined,
      onRiskScoresReceived: () => undefined,
      onReminderOutcomeReceived: () => undefined,
      onAppointmentStatusUpdateReceived: () => undefined,
      handleRiskCommand: () => undefined,
    };
  }

  return {
    enabled,
    machineState,
    riskScoresMap,
    getReminderState,
    requestRiskScores,
    onRiskScoresReceived,
    onReminderOutcomeReceived,
    onAppointmentStatusUpdateReceived,
    handleRiskCommand,
  };
}
